import {
  Component,
  Input,
  forwardRef,
  ElementRef,

  HostBinding,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Renderer2,
  Inject,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  NgZone,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import dayjs from 'dayjs';

interface CalendarDay {
  date: dayjs.Dayjs;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isInRange: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
}

export type SelectionMode = 'single' | 'multiple' | 'range' | 'extended' | 'period';

export interface DateRange {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
}

interface MonthOption {
  value: number;
  name: string;
}

type ViewMode = 'day' | 'month' | 'year';

/**
 * Full-featured datepicker (CVA), ported from the kdc_registry `jhi-datepicker`
 * (Workstream D shared UI). Selection modes: single | multiple | range |
 * extended (range + presets sidebar) | period (dual-pane year->month->day);
 * day/month/year views, Chrome-style segmented keyboard input, and a
 * body-appended fixed-position dropdown.
 *
 * NOTE: unlike the other App* primitives this component keeps Default change
 * detection. The source implementation is imperative (manual
 * `cdr.detectChanges()`, body-appended embedded views repositioned via a
 * `requestAnimationFrame` loop outside the Angular zone, `setTimeout`-driven
 * field selection); OnPush breaks those flows, so it stays on Default.
 *
 * <app-date-picker [(ngModel)]="date"></app-date-picker>
 * <app-date-picker selectionMode="range" formControlName="period"></app-date-picker>
 */
@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, NgTemplateOutlet],
  templateUrl: './app-date-picker.component.html',
  styleUrls: ['./app-date-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDatePickerComponent),
      multi: true,
    },
  ],
})
export class AppDatePickerComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @HostBinding('style.zIndex')
  get zIndex(): string {
    return this.isOpen ? '1001' : 'auto';
  }

  @HostBinding('style.position') position = 'relative';

  // Inputs
  @Input() minDate: dayjs.Dayjs | null = null;
  @Input() maxDate: dayjs.Dayjs | null = null;
  @Input() disabledDates: dayjs.Dayjs[] = [];
  @Input() disabledRanges: { start: dayjs.Dayjs; end: dayjs.Dayjs }[] = [];
  @Input() placeholder = 'dd-mm-yyyy';
  @Input() disabled = false;
  @Input() minMode: 'day' | 'month' | 'year' = 'day';
  @Input() selectionMode: SelectionMode = 'single';
  @Input() valueFormat: string | null = null;
  @Input() numberValue = false;
  @Input() static = false;
  @Input() count: number | null = null;
  /**
   * Host-driven error state: when true the input shows the danger border
   * regardless of the form-control's own ng-invalid/ng-touched status. Lets a
   * parent surface a validation failure only when it wants to (e.g. on submit),
   * decoupled from the touched-based default styling.
   */
  @Input() invalid = false;
  /**
   * After a single-date pick (calendar click / Today) closes the calendar,
   * move focus to the next focusable field — same convenience as app-lookup's
   * Enter/click commit. Off by default so report-screen pickers are unchanged.
   */
  @Input() advanceFocusOnSelect = false;
  /**
   * Compact mode: shrinks the input to Bootstrap form-control-sm height
   * (~31px) so the picker lines up with small form fields. Off by default.
   */
  @Input() small = false;

  // Test Data for Datepicker
  // testDisabledDates: Dayjs[] = [
  //     dayjs().date(5), // Disable 5th of current month
  //     dayjs().date(10) // Disable 10th of current month
  // ];
  // testDisabledRanges: { start: Dayjs; end: Dayjs }[] = [
  //     { start: dayjs().date(15), end: dayjs().date(20) } // Disable 15th to 20th
  // ];

  // ViewChild for the dropdown template
  @ViewChild('dropdownTemplate', { static: true }) dropdownTemplate!: TemplateRef<unknown>;

  @Output() dateSelect = new EventEmitter<any>();

  // State
  isOpen = false;
  inputValue = '';
  viewDate: dayjs.Dayjs = dayjs();

  // Selection State
  selectedDate: dayjs.Dayjs | null = null; // for single
  selectedDates: dayjs.Dayjs[] = []; // for multiple
  @Input() rangeStart: dayjs.Dayjs | null = null; // for range/extended
  @Input() rangeEnd: dayjs.Dayjs | null = null; // for range/extended
  hoverDate: dayjs.Dayjs | null = null; // for highlighting range

  calendarDays: CalendarDay[] = [];
  weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  // Presets for Extended Mode
  presets = [
    { label: 'Current Month', value: 'thisMonth' },
    { label: 'Previous Month', value: 'lastMonth' },
    { label: 'Current Year', value: 'thisYear' },
    { label: 'Previous Year', value: 'lastYear' },
    { label: 'Fiscal Year', value: 'fiscalYear' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 30 Days', value: 'last30days' },
  ];

  // View mode: day, month, year
  viewMode: ViewMode = 'day';

  // Active Preset Highlight
  activePreset: string | null = null;

  // Month picker data
  months: MonthOption[] = [
    { value: 0, name: 'Jan' },
    { value: 1, name: 'Feb' },
    { value: 2, name: 'Mar' },
    { value: 3, name: 'Apr' },
    { value: 4, name: 'May' },
    { value: 5, name: 'Jun' },
    { value: 6, name: 'Jul' },
    { value: 7, name: 'Aug' },
    { value: 8, name: 'Sep' },
    { value: 9, name: 'Oct' },
    { value: 10, name: 'Nov' },
    { value: 11, name: 'Dec' },
  ];

  // Year picker data
  yearRange: number[] = [];
  yearRangeStart = 0;
  yearRangeEnd = 0;

  // Period Mode State (Dual View)
  periodStartViewMode: ViewMode = 'year';
  periodEndViewMode: ViewMode = 'year';

  periodStartViewDate: dayjs.Dayjs = dayjs();
  periodEndViewDate: dayjs.Dayjs = dayjs(); // Defaults to same or next? User said "Initial view of both parts are Year view"

  periodStartYearRange: number[] = [];
  periodStartYearRangeStart = 0;

  periodEndYearRange: number[] = [];
  periodEndYearRangeStart = 0;

  periodStartCalendarDays: CalendarDay[] = [];
  periodEndCalendarDays: CalendarDay[] = [];

  // Dropdown position for fixed positioning
  dropdownStyle: { [key: string]: string } = {
    visibility: 'hidden',
    position: 'fixed',
    zIndex: '99999',
  };

  // Input field state (Chrome-style)
  private inputElement: HTMLInputElement | null = null;
  private currentField: 'day' | 'month' | 'year' = 'day';
  private currentRangeSide: 'start' | 'end' = 'start';
  private dayValue = '';
  private monthValue = '';
  private yearValue = '';

  // End date fields for range mode
  private endDayValue = '';
  private endMonthValue = '';
  private endYearValue = '';

  get isInputReadonly(): boolean {
    return this.selectionMode === 'multiple' || this.minMode === 'month';
  }

  // For body-appended dropdown
  private dropdownElement: HTMLElement | null = null;
  private embeddedViewRef: ReturnType<ViewContainerRef['createEmbeddedView']> | null = null;
  private animationFrame: number | null = null;


  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    // class="form-control form-control-sm" on the HOST mirrors a native small
    // input: the :host(.form-control/.form-control-sm) css strips the host's
    // own Bootstrap box (the inner input already carries form-control), and
    // the -sm marker switches the picker into compact ([small]) mode.
    const hostClasses = (this.elementRef.nativeElement as HTMLElement).classList;
    if (hostClasses && hostClasses.contains('form-control-sm')) {
      this.small = true;
    }

    this.generateCalendar();
    this.updateYearRange();

    // set default placeholder for period/range modes if not provided
    if (
      (this.selectionMode === 'period' || this.selectionMode === 'range' || this.selectionMode === 'extended') &&
      this.placeholder === 'dd-mm-yyyy'
    ) {
      this.placeholder = this.isMonthPeriod ? 'mmm yyyy to mmm yyyy' : 'dd-mm-yyyy to dd-mm-yyyy';
    } else if (this.minMode === 'month' && this.placeholder === 'dd-mm-yyyy') {
      this.placeholder = 'Month Year';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('minDate' in changes || 'maxDate' in changes) {
      this.generateCalendar();
    }
    if ('rangeStart' in changes && this.rangeStart) {
      if (typeof this.rangeStart === 'string') {
        this.rangeStart = dayjs(this.rangeStart);
      }
    }
    if ('rangeEnd' in changes && this.rangeEnd) {
      if (typeof this.rangeEnd === 'string') {
        this.rangeEnd = dayjs(this.rangeEnd);
      }
    }
    if ('rangeStart' in changes || 'rangeEnd' in changes) {
      this.syncStateToAtoms();
    }
  }

  ngOnDestroy(): void {
    this.unbindOutsideClick();
    this.destroyDropdown();
    if (AppDatePickerComponent.openInstance === this) {
      AppDatePickerComponent.openInstance = null;
    }
  }

  // Outside-click close is bound in capture phase AFTER open (see bindOutsideClick),
  // so the opening click itself never closes us. HostListener was racing the open
  // gesture inside AppModal and produced the flash-then-gone calendar.
  private outsideClickUnlisten: (() => void) | null = null;

  private bindOutsideClick(): void {
    this.unbindOutsideClick();
    // Defer one macrotask so the opening click is fully finished.
    setTimeout(() => {
      if (!this.isOpen) {
        return;
      }
      this.outsideClickUnlisten = this.renderer.listen('document', 'mousedown', (event: MouseEvent) => {
        const target = event.target as Node;
        const clickedInside = this.elementRef.nativeElement.contains(target);
        const clickedOnDropdown = this.dropdownElement?.contains(target);
        if (!clickedInside && !clickedOnDropdown && this.isOpen) {
          if (this.selectionMode === 'period' && this.rangeStart && !this.rangeEnd) {
            this.rangeStart = null;
            this.dayValue = '';
            this.monthValue = '';
            this.yearValue = '';
            this.updateInputDisplay();
            this.emitChange(null);
          }
          this.isOpen = false;
          this.viewMode = this.minMode;
          this.destroyDropdown();
          this.cdr.markForCheck();
        }
      });
    }, 0);
  }

  private unbindOutsideClick(): void {
    if (this.outsideClickUnlisten) {
      this.outsideClickUnlisten();
      this.outsideClickUnlisten = null;
    }
  }

  // ============== GETTERS ==============

  /**
   * Month-granularity period: selectionMode="period" + minMode="month". Each
   * side completes at the MONTH grid (no day view) — picking July 2026 as the
   * start means 01-Jul-2026, picking June 2027 as the end means 30-Jun-2027 —
   * and the input reads "Jul 2026 to Jun 2027". Used by the panchang
   * calendar's filter window.
   */
  get isMonthPeriod(): boolean {
    return this.selectionMode === 'period' && this.minMode === 'month';
  }

  get currentMonthName(): string {
    return this.viewDate.format('MMMM');
  }

  get currentYear(): number {
    return this.viewDate.year();
  }

  get monthYearLabel(): string {
    return this.viewDate.format('MMMM YYYY');
  }

  // ============== INPUT HANDLING (Chrome-style) ==============

  onInputFocus(event: FocusEvent): void {
    if (this.isInputReadonly) {
      return;
    }
    this.inputElement = event.target as HTMLInputElement;

    // Select initial field based on minMode
    if (this.minMode === 'year') {
      this.currentField = 'year';
    } else if (this.minMode === 'month') {
      this.currentField = 'month';
    } else {
      this.currentField = 'day';
    }

    this.updateInputDisplay();

    // If all fields are empty, trigger parsed input logic to set null
    this.updateInputDisplay();

    if (this.selectionMode === 'multiple' || this.selectionMode === 'range' || this.selectionMode === 'extended') {
      // Don't auto-select single fields for advanced modes
      return;
    }

    setTimeout(() => this.selectCurrentField());
  }

  onInputClick(event: MouseEvent): void {
    // Always open the calendar on input click (single/day mode included) —
    // inside AppModal the icon-only open was easy to miss and the field focus
    // alone did nothing visible.
    if (this.selectionMode === 'single' || this.isInputReadonly) {
      event.stopPropagation();
      if (!this.isOpen) {
        this.toggleCalendar(event);
      }
      if (this.isInputReadonly) {
        return;
      }
    }
    const input = event.target as HTMLInputElement;
    const pos = input.selectionStart ?? 0;

    // In multiple mode, treat as standard input or handle differently
    if (this.selectionMode === 'multiple') {
      // Allow default behavior
      return;
    }

    if (this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period') {
      // Format: dd-mm-yyyy to dd-mm-yyyy
      // Lengths: Start=10, Separator=4 (" to "), End=10. Total=24.
      // Start: 0-10
      // End: 14-24
      if (pos <= 12) {
        this.currentRangeSide = 'start';
      } else {
        this.currentRangeSide = 'end';
      }
    } else {
      this.currentRangeSide = 'start';
    }

    if (this.minMode === 'year') {
      this.currentField = 'year';
    } else if (this.minMode === 'month') {
      // Format: mm-yyyy (positions: 0-1=month, 3-6=year)
      // Range relative pos: 0-1=month, 3-6=year
      const relativePos = this.currentRangeSide === 'end' ? pos - 14 : pos;
      if (relativePos <= 2) {
        this.currentField = 'month';
      } else {
        this.currentField = 'year'; // > 2
      }
    } else {
      // Format: dd-mm-yyyy (positions: 0-1=day, 3-4=month, 6-9=year)
      const relativePos = this.currentRangeSide === 'end' ? pos - 14 : pos;
      if (relativePos <= 2) {
        this.currentField = 'day';
      } else if (relativePos <= 5) {
        this.currentField = 'month';
      } else {
        this.currentField = 'year';
      }
    }
    setTimeout(() => this.selectCurrentField());
  }

  onInputKeyDown(event: KeyboardEvent): void {
    // Month-period input is a readonly DISPLAY — no segment typing. The digit
    // machinery would still mutate the atoms behind the readonly input.
    if (this.isMonthPeriod) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const key = event.key;

    // Handle arrow keys for field navigation
    if (key === 'ArrowLeft') {
      event.preventDefault();
      this.moveToPreviousField();
      return;
    }
    if (key === 'ArrowRight') {
      event.preventDefault();
      this.moveToNextField();
      return;
    }

    // Handle Tab - let default behavior work but track field
    if (key === 'Tab') {
      return;
    }

    // Handle Enter
    if (key === 'Enter') {
      this.parseAndApplyInput();
      return;
    }

    // Handle Backspace/Delete
    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      this.clearCurrentField();
      return;
    }

    // Bypass strict masking for multiple/range modes to allow free typing (commas, etc.)
    if (this.selectionMode === 'multiple') {
      return;
    }

    // Only allow digits for single mode masking
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.handleDigitInput(key);
  }

  onInputChange(): void {
    // Month-period: readonly input, nothing to parse — a blur used to re-emit
    // the current range through parseAndApplyInput, which the panchang filter
    // treated as a USER-picked range (spuriously switching to list mode).
    if (this.isMonthPeriod) {
      return;
    }
    // Called on blur
    this.parseAndApplyInput();

    // If Period mode and selection is incomplete (Start set but End missing), clear relevant state
    if (this.selectionMode === 'period' && this.rangeStart && !this.rangeEnd) {
      this.rangeStart = null;
      this.dayValue = '';
      this.monthValue = '';
      this.yearValue = '';
      this.updateInputDisplay();
      this.emitChange(null);
    }
  }

  // ============== CALENDAR GENERATION ==============

  generateCalendar(): void {
    this.calendarDays = this.getDaysForMonth(this.viewDate);
  }

  onDayHover(day: CalendarDay): void {
    if ((this.selectionMode === 'range' || this.selectionMode === 'extended') && this.rangeStart && !this.rangeEnd) {
      this.hoverDate = day.date;
      this.generateCalendar(); // Re-render for hover effect
    }
  }

  selectPreset(preset: string): void {
    const today = dayjs();
    let start: dayjs.Dayjs | null = null;
    let end: dayjs.Dayjs | null = null;

    switch (preset) {
      case 'today':
        start = end = today;
        break;
      case 'yesterday':
        start = end = today.subtract(1, 'day');
        break;
      case 'thisMonth':
        start = today.startOf('month');
        end = today.endOf('month');
        break;
      case 'lastMonth':
        start = today.subtract(1, 'month').startOf('month');
        end = today.subtract(1, 'month').endOf('month');
        break;
      case 'thisYear':
        start = today.startOf('year');
        end = today.endOf('year');
        break;
      case 'lastYear':
        start = today.subtract(1, 'year').startOf('year');
        end = today.subtract(1, 'year').endOf('year');
        break;
      case 'fiscalYear': {
        // Last Completed Financial Year (Apr 1 - Mar 31)
        const currentMonth = today.month(); // 0-11
        let startYear: number;
        let endYear: number;

        if (currentMonth >= 3) {
          // April onwards
          startYear = today.year() - 1;
          endYear = today.year();
        } else {
          // Jan, Feb, Mar
          startYear = today.year() - 2;
          endYear = today.year() - 1;
        }

        start = dayjs(`${startYear}-04-01`);
        end = dayjs(`${endYear}-03-31`);
        break;
      }
      case 'last7days':
        start = today.subtract(6, 'day');
        end = today;
        break;
      case 'last30days':
        start = today.subtract(29, 'day');
        end = today;
        break;
    }

    if (start && end) {
      // Clamp to min/max
      if (this.minDate) {
        if (start.isBefore(this.minDate, 'day')) {
          start = this.minDate;
        }
        if (end.isBefore(this.minDate, 'day')) {
          end = this.minDate;
        }
      }
      if (this.maxDate) {
        if (start.isAfter(this.maxDate, 'day')) {
          start = this.maxDate;
        }
        if (end.isAfter(this.maxDate, 'day')) {
          end = this.maxDate;
        }
      }

      // Validate sequence
      if (start.isAfter(end, 'day')) {
        // Invalid range after clamping (e.g., preset entirely out of bounds)
        return;
      }

      this.rangeStart = start;
      this.rangeEnd = end;
      this.activePreset = preset;
      this.viewDate = start; // Jump to the start date's month

      // Sync atoms
      this.dayValue = start.format('DD');
      this.monthValue = start.format('MM');
      this.yearValue = start.format('YYYY');
      this.endDayValue = end.format('DD');
      this.endMonthValue = end.format('MM');
      this.endYearValue = end.format('YYYY');

      this.updateInputDisplay();
      this.emitChange({ start: this.rangeStart, end: this.rangeEnd });
      this.generateCalendar();
    }
  }

  // ============== VIEW MODE SWITCHING ==============

  showMonthPicker(): void {
    if (this.viewMode === 'month') {
      this.viewMode = 'day';
    } else {
      this.viewMode = 'month';
    }
  }

  showYearPicker(): void {
    if (this.viewMode === 'year') {
      this.viewMode = 'day';
    } else {
      this.updateYearRange();
      this.viewMode = 'year';
    }
  }

  selectMonth(month: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.viewDate = this.viewDate.month(month);

    if (this.minMode === 'month') {
      // If month picker, select the month (first day of month usually)
      this.selectDate({
        date: this.viewDate.startOf('month'),
        day: 1,
        isCurrentMonth: true,
        isToday: false,
        isSelected: true,
        isDisabled: false,
        isInRange: false,
        isRangeStart: false,
        isRangeEnd: false,
      });
    } else {
      this.generateCalendar();
      this.viewMode = 'day';
    }
  }

  selectYear(year: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.viewDate = this.viewDate.year(year);

    if (this.minMode === 'year') {
      // If year picker, select the year (first day of year)
      this.selectDate({
        date: this.viewDate.startOf('year'),
        day: 1,
        isCurrentMonth: true,
        isToday: false,
        isSelected: true,
        isDisabled: false,
        isInRange: false,
        isRangeStart: false,
        isRangeEnd: false,
      });
    } else {
      this.generateCalendar();
      this.viewMode = 'month';
    }
  }

  updateYearRange(): void {
    const currentYear = this.viewDate.year();
    this.yearRangeStart = Math.floor(currentYear / 12) * 12;
    this.yearRangeEnd = this.yearRangeStart + 11;
    this.yearRange = [];
    for (let y = this.yearRangeStart; y <= this.yearRangeEnd; y++) {
      this.yearRange.push(y);
    }
  }

  // ============== PERIOD MODE LOGIC ==============

  prevYearRange(): void {
    this.yearRangeStart -= 12;
    this.yearRangeEnd -= 12;
    this.yearRange = [];
    for (let y = this.yearRangeStart; y <= this.yearRangeEnd; y++) {
      this.yearRange.push(y);
    }
  }

  nextYearRange(): void {
    this.yearRangeStart += 12;
    this.yearRangeEnd += 12;
    this.yearRange = [];
    for (let y = this.yearRangeStart; y <= this.yearRangeEnd; y++) {
      this.yearRange.push(y);
    }
  }

  /** Period pane TITLE click: day→month→year drill-up. Entering the year
   *  view must (re)compute that side's 12-year page — the old inline template
   *  expression only flipped the mode, so a pane that OPENED in month view
   *  (month-granularity period) showed an empty "0 -" year grid. */
  togglePeriodTitleView(side: 'start' | 'end'): void {
    const mode = side === 'start' ? this.periodStartViewMode : this.periodEndViewMode;
    const next: ViewMode = mode === 'day' ? 'month' : 'year';
    if (side === 'start') {
      this.periodStartViewMode = next;
    } else {
      this.periodEndViewMode = next;
    }
    if (next === 'year') {
      this.updatePeriodYearRange(side);
    }
  }

  selectPeriodYear(side: 'start' | 'end', year: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (side === 'start') {
      this.periodStartViewDate = this.periodStartViewDate.year(year);
      this.periodStartViewMode = 'month';
    } else {
      this.periodEndViewDate = this.periodEndViewDate.year(year);
      this.periodEndViewMode = 'month';
    }
  }

  selectPeriodMonth(side: 'start' | 'end', month: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (side === 'start') {
      if (this.isMonthPeriod) {
        // month IS the selection: start = first day of the picked month; the
        // end pane jumps straight to a month grid (its year chip/arrows cover
        // cross-year ranges). NO mid-pick emit — the parent's [rangeStart]/
        // [rangeEnd] bindings would stomp the in-progress pick through
        // ngOnChanges before the end month is chosen.
        if (this.isPeriodMonthDisabled(month, 'start')) {
          return;
        }
        this.periodStartViewDate = this.periodStartViewDate.month(month);
        this.rangeStart = this.periodStartViewDate.startOf('month');
        this.rangeEnd = null;
        this.activePreset = null;
        this.periodEndViewDate = this.rangeStart.clone();
        this.periodEndViewMode = 'month';
        this.syncStateToAtoms();
        return;
      }
      this.periodStartViewDate = this.periodStartViewDate.month(month);
      this.periodStartViewMode = 'day';
      this.generatePeriodCalendar();
    } else {
      if (this.isMonthPeriod) {
        if (!this.rangeStart || this.isPeriodMonthDisabled(month, 'end')) {
          return;
        }
        const end = this.periodEndViewDate.month(month).endOf('month');
        if (end.isBefore(this.rangeStart, 'day')) {
          return;
        }
        this.periodEndViewDate = this.periodEndViewDate.month(month);
        this.rangeEnd = end;
        this.activePreset = null;
        this.syncStateToAtoms();
        // complete range: close and emit (mirrors selectPeriodDate's end pick)
        this.isOpen = false;
        if (AppDatePickerComponent.openInstance === this) {
          AppDatePickerComponent.openInstance = null;
        }
        this.destroyDropdown();
        this.emitChange({ start: this.rangeStart, end: this.rangeEnd });
        this.onTouched();
        return;
      }
      this.periodEndViewDate = this.periodEndViewDate.month(month);
      this.periodEndViewMode = 'day';
      this.generatePeriodCalendar();
    }
  }

  selectPeriodDate(side: 'start' | 'end', day: CalendarDay): void {
    if (day.isDisabled) {
      return;
    }

    if (side === 'start') {
      // Always clear End Date when Start Date changes to enforce sequential selection
      this.rangeEnd = null;
      this.rangeStart = day.date;
      this.activePreset = null;

      // Move "To Date" view to valid region (Start Date's view)
      this.periodEndViewDate = this.rangeStart.clone();

      // Clear end input fields
      this.endDayValue = '';
      this.endMonthValue = '';
      this.endYearValue = '';
      this.updateInputDisplay();

      // Notify change with partial selection (end is null)
      this.emitChange(null);

      // Reset to Year View as requested
      this.periodEndViewMode = 'year';
      this.updatePeriodYearRange('end');
    } else {
      if (this.rangeStart && day.date.isBefore(this.rangeStart, 'day')) {
        return;
      }
      this.rangeEnd = day.date;
      this.activePreset = null;

      // Auto-close on End Date selection as requested
      this.isOpen = false;
      this.destroyDropdown();

      // Emit the selected period
      this.emitChange({ start: this.rangeStart, end: this.rangeEnd });
    }

    if (this.rangeStart) {
      this.dayValue = this.rangeStart.format('DD');
      this.monthValue = this.rangeStart.format('MM');
      this.yearValue = this.rangeStart.format('YYYY');
    }
    if (this.rangeEnd) {
      this.endDayValue = this.rangeEnd.format('DD');
      this.endMonthValue = this.rangeEnd.format('MM');
      this.endYearValue = this.rangeEnd.format('YYYY');
    } else {
      this.endDayValue = '';
      this.endMonthValue = '';
      this.endYearValue = '';
    }

    this.updateInputDisplay();
    this.emitChange({ start: this.rangeStart, end: this.rangeEnd });
    this.generatePeriodCalendar();
  }

  isPeriodYearDisabled(year: number, side: 'start' | 'end'): boolean {
    if (side === 'end') {
      const startYear = this.rangeStart ? this.rangeStart.year() : this.periodStartViewDate.year();
      if (year < startYear) {
        return true;
      }
    }
    if (this.minDate && year < this.minDate.year()) {
      return true;
    }
    if (this.maxDate && year > this.maxDate.year()) {
      return true;
    }
    return false;
  }

  isPeriodMonthDisabled(month: number, side: 'start' | 'end'): boolean {
    const viewDate = side === 'start' ? this.periodStartViewDate : this.periodEndViewDate;
    const testDate = viewDate.month(month).startOf('month');
    if (side === 'end') {
      // New Rule: If Start Pane is showing a specific month (Day Mode), disable months before it
      if (this.periodStartViewMode === 'day') {
        if (testDate.isBefore(this.periodStartViewDate, 'month')) {
          return true;
        }
      }
      if (this.rangeStart) {
        if (testDate.year() === this.rangeStart.year() && month < this.rangeStart.month()) {
          return true;
        }
        if (testDate.year() < this.rangeStart.year()) {
          return true;
        }
      }
    }
    if (this.minDate && testDate.isBefore(this.minDate.startOf('month'))) {
      return true;
    }
    if (this.maxDate && testDate.isAfter(this.maxDate.endOf('month'))) {
      return true;
    }
    return false;
  }

  prevPeriodYearRange(side: 'start' | 'end'): void {
    if (side === 'start') {
      this.periodStartViewDate = this.periodStartViewDate.subtract(12, 'year');
      this.updatePeriodYearRange('start');
    } else {
      this.periodEndViewDate = this.periodEndViewDate.subtract(12, 'year');
      this.updatePeriodYearRange('end');
    }
  }

  nextPeriodYearRange(side: 'start' | 'end'): void {
    if (side === 'start') {
      this.periodStartViewDate = this.periodStartViewDate.add(12, 'year');
      this.updatePeriodYearRange('start');
    } else {
      this.periodEndViewDate = this.periodEndViewDate.add(12, 'year');
      this.updatePeriodYearRange('end');
    }
  }

  prevPeriodView(side: 'start' | 'end'): void {
    const viewMode = side === 'start' ? this.periodStartViewMode : this.periodEndViewMode;
    const viewDate = side === 'start' ? this.periodStartViewDate : this.periodEndViewDate;

    const unit = viewMode === 'day' ? 'month' : 'year';
    const newDate = viewDate.subtract(1, unit);

    if (side === 'start') {
      this.periodStartViewDate = newDate;
    } else {
      this.periodEndViewDate = newDate;
    }
    this.generatePeriodCalendar();
  }

  nextPeriodView(side: 'start' | 'end'): void {
    const viewMode = side === 'start' ? this.periodStartViewMode : this.periodEndViewMode;
    const viewDate = side === 'start' ? this.periodStartViewDate : this.periodEndViewDate;

    const unit = viewMode === 'day' ? 'month' : 'year';
    const newDate = viewDate.add(1, unit);

    if (side === 'start') {
      this.periodStartViewDate = newDate;
    } else {
      this.periodEndViewDate = newDate;
    }
    this.generatePeriodCalendar();
  }

  // ============== NAVIGATION VISIBILITY ==============

  canGoPrevMonth(): boolean {
    if (!this.minDate) {
      return true;
    }
    const prevMonth = this.viewDate.subtract(1, 'month').endOf('month');
    return prevMonth.isAfter(this.minDate, 'day') || prevMonth.isSame(this.minDate, 'day');
  }

  canGoNextMonth(): boolean {
    if (!this.maxDate) {
      return true;
    }
    const nextMonth = this.viewDate.add(1, 'month').startOf('month');
    return nextMonth.isBefore(this.maxDate, 'day') || nextMonth.isSame(this.maxDate, 'day');
  }

  canGoPrevYearRange(): boolean {
    if (!this.minDate) {
      return true;
    }
    return this.yearRangeStart > this.minDate.year();
  }

  canGoNextYearRange(): boolean {
    if (!this.maxDate) {
      return true;
    }
    return this.yearRangeEnd < this.maxDate.year();
  }

  canGoPeriodPrev(side: 'start' | 'end'): boolean {
    const viewMode = side === 'start' ? this.periodStartViewMode : this.periodEndViewMode;
    const viewDate = side === 'start' ? this.periodStartViewDate : this.periodEndViewDate;

    if (side === 'start') {
      // Check minDate
      if (viewMode === 'day' || viewMode === 'month') {
        const unit = viewMode === 'day' ? 'month' : 'year';
        const prevDate = viewDate.subtract(1, unit).endOf(unit);
        if (this.minDate && prevDate.isBefore(this.minDate, 'day')) {
          return false;
        }
      } else {
        // Year Range
        if (this.minDate && this.periodStartYearRangeStart - 12 < this.minDate.year()) {
          // It's a bit loose for year range, just check if we can go back
          if (this.periodStartYearRangeStart <= this.minDate.year()) {
            return false;
          }
        }
      }
      return true;
    } else {
      // SIDE END
      // Constraint: Cannot go before Start View
      if (viewMode === 'day' || viewMode === 'month') {
        const unit = viewMode === 'day' ? 'month' : 'year';
        const prevDate = viewDate.subtract(1, unit);

        // Compare with Start View Date explicitly
        // If Start View is Day (e.g. May 2018), checking Month Granularity
        const compareUnit = this.periodStartViewMode === 'day' ? 'month' : 'year';
        if (prevDate.isBefore(this.periodStartViewDate, compareUnit)) {
          return false;
        }
      } else {
        // Year Range
        // Start Range: [startYear, startYear+11]
        // End Range Prev: [endYear-12, endYear-1]
        if (this.periodStartViewMode === 'year') {
          if (this.periodEndYearRangeStart - 12 < this.periodStartYearRangeStart) {
            return false;
          }
        } else {
          // Start View is Day/Month (Specific Year)
          // If Previous Range End (endYear-1) < Start View Year, disable
          const prevRangeEnd = this.periodEndYearRangeStart - 1;
          const startViewYear = this.periodStartViewDate.year();
          if (prevRangeEnd < startViewYear) {
            return false;
          }
        }
      }

      // Check minDate (less critical for End side usually, but good practice)
      if (viewMode === 'day' || viewMode === 'month') {
        const unit = viewMode === 'day' ? 'month' : 'year';
        const prevDate = viewDate.subtract(1, unit).endOf(unit);
        if (this.minDate && prevDate.isBefore(this.minDate, 'day')) {
          return false;
        }
      }
      return true;
    }
  }

  canGoPeriodNext(side: 'start' | 'end'): boolean {
    const viewMode = side === 'start' ? this.periodStartViewMode : this.periodEndViewMode;
    const viewDate = side === 'start' ? this.periodStartViewDate : this.periodEndViewDate;

    if (side === 'end') {
      // Check maxDate
      if (viewMode === 'day' || viewMode === 'month') {
        const unit = viewMode === 'day' ? 'month' : 'year';
        const nextDate = viewDate.add(1, unit).startOf(unit);
        if (this.maxDate && nextDate.isAfter(this.maxDate, 'day')) {
          return false;
        }
      } else {
        if (this.maxDate && this.periodEndYearRange[this.periodEndYearRange.length - 1] + 12 > this.maxDate.year()) {
          if (this.periodEndYearRange[this.periodEndYearRange.length - 1] + 1 > this.maxDate.year()) {
            return false;
          }
        }
      }
      return true;
    } else {
      // SIDE START
      // Constraint: Can go forward freely (bounded by maxDate only)
      // Left Pane navigation should not be blocked by Right Pane view in strict sequential mode
      // because selecting a new Start Date will reset the Right Pane anyway.

      // Check maxDate
      if (viewMode === 'day' || viewMode === 'month') {
        const unit = viewMode === 'day' ? 'month' : 'year';
        const nextDate = viewDate.add(1, unit).startOf(unit);
        if (this.maxDate && nextDate.isAfter(this.maxDate, 'day')) {
          return false;
        }
      } else {
        if (this.maxDate && this.periodStartYearRange[this.periodStartYearRange.length - 1] + 12 > this.maxDate.year()) {
          if (this.periodStartYearRange[this.periodStartYearRange.length - 1] + 1 > this.maxDate.year()) {
            return false;
          }
        }
      }

      return true;
    }
  }

  isYearDisabled(year: number): boolean {
    if (this.minDate && year < this.minDate.year()) {
      return true;
    }
    if (this.maxDate && year > this.maxDate.year()) {
      return true;
    }
    return false;
  }

  isMonthDisabled(month: number): boolean {
    const year = this.viewDate.year();
    // Check against minDate
    if (this.minDate) {
      if (year < this.minDate.year()) {
        return true;
      }
      if (year === this.minDate.year() && month < this.minDate.month()) {
        return true;
      }
    }
    // Check against maxDate
    if (this.maxDate) {
      if (year > this.maxDate.year()) {
        return true;
      }
      if (year === this.maxDate.year() && month > this.maxDate.month()) {
        return true;
      }
    }
    return false;
  }

  // ============== CALENDAR ACTIONS ==============

  /** The one currently-open picker app-wide. The toggle button stopPropagation()s
   *  its clicks, so OTHER pickers' document outside-click handlers never fire —
   *  opening a second picker must explicitly close the first via this registry. */
  private static openInstance: AppDatePickerComponent | null = null;

  toggleCalendar(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.isOpen = false;
      if (AppDatePickerComponent.openInstance === this) {
        AppDatePickerComponent.openInstance = null;
      }
      this.viewMode = this.minMode;
      this.destroyDropdown();
    } else {
      const other = AppDatePickerComponent.openInstance;
      if (other && other !== this) {
        other.closeCalendar();
        other.cdr.detectChanges();
      }
      AppDatePickerComponent.openInstance = this;
      this.isOpen = true;
      this.viewMode = this.minMode;
      if (this.selectedDate) {
        this.viewDate = this.selectedDate;
      }
      if (this.selectionMode === 'period') {
        // month-granularity opens straight at the month grids (the year is
        // one tap away on the pane title); day-granularity keeps year-first
        this.periodStartViewMode = this.isMonthPeriod ? 'month' : 'year';
        this.periodEndViewMode = this.isMonthPeriod ? 'month' : 'year';
        this.periodStartViewDate = this.rangeStart ? this.rangeStart : dayjs();
        this.periodEndViewDate = this.rangeEnd ? this.rangeEnd : this.periodStartViewDate;
        this.generatePeriodCalendar();
      } else {
        this.generateCalendar();
      }
      this.updateYearRange();
      this.createDropdown();
      // Static/inline: fix the absolute-visible style BEFORE the first render so
      // the outlet never paints a frame with the initial visibility:hidden.
      if (this.static) {
        this.updateDropdownPosition();
      }
      this.cdr.detectChanges();
      if (this.embeddedViewRef) {
        this.embeddedViewRef.detectChanges();
      }
      // Position NOW (panel is opacity:0 until positioned) then reveal.
      this.updateDropdownPosition();
      this.revealDropdown();
      this.bindOutsideClick();
    }
  }

  prevMonth(): void {
    this.viewDate = this.viewDate.subtract(1, 'month');
    this.generateCalendar();
  }

  nextMonth(): void {
    this.viewDate = this.viewDate.add(1, 'month');
    this.generateCalendar();
  }

  selectDate(day: CalendarDay): void {
    if (day.isDisabled) {
      return;
    }

    if (this.selectionMode === 'single') {
      this.selectedDate = day.date;
      // Sync fields
      this.dayValue = day.date.format('DD');
      this.monthValue = day.date.format('MM');
      this.yearValue = day.date.format('YYYY');
      this.updateInputDisplay();
      this.emitChange(this.selectedDate);
      this.isOpen = false;
      this.destroyDropdown();
      if (this.advanceFocusOnSelect) {
        this.focusNextFocusable();
      }
    } else if (this.selectionMode === 'multiple') {
      const index = this.selectedDates.findIndex(d => d.isSame(day.date, 'day'));
      if (index >= 0) {
        this.selectedDates.splice(index, 1);
      } else {
        if (this.count && this.selectedDates.length >= this.count) {
          // Limit reached, do nothing or replace?
          // Requirement: "If we set count = 2, we can select 2 days only."
          // Usually implies we can't select more.
          return;
        }
        this.selectedDates.push(day.date);
      }
      // Sort dates chronologically
      this.selectedDates.sort((a, b) => a.valueOf() - b.valueOf());

      this.updateInputDisplay();
      this.emitChange(this.selectedDates);
      // Don't close dropdown in multiple mode
    } else {
      if (!this.rangeStart || this.rangeEnd) {
        this.rangeStart = day.date;
        this.rangeEnd = null;
        this.hoverDate = null;
        this.rangeStart = day.date;
        this.rangeEnd = null;
        this.hoverDate = null;
        this.activePreset = null; // Clear preset if manual selection starts
      } else {
        // We have a start, checking end
        if (day.date.isBefore(this.rangeStart)) {
          this.rangeEnd = this.rangeStart;
          this.rangeStart = day.date;
        } else {
          this.rangeEnd = day.date;
        }
        this.emitChange({ start: this.rangeStart, end: this.rangeEnd });
        this.activePreset = null; // Clear preset on manual range completion
      }
      // Sync values to atoms
      // rangeStart is always set in this path
      this.dayValue = this.rangeStart.format('DD');
      this.monthValue = this.rangeStart.format('MM');
      this.yearValue = this.rangeStart.format('YYYY');
      if (this.rangeEnd) {
        this.endDayValue = this.rangeEnd.format('DD');
        this.endMonthValue = this.rangeEnd.format('MM');
        this.endYearValue = this.rangeEnd.format('YYYY');
      } else {
        this.endDayValue = '';
        this.endMonthValue = '';
        this.endYearValue = '';
      }
      this.updateInputDisplay();
    }

    this.generateCalendar();
    this.onTouched();
  }

  selectToday(): void {
    const today = dayjs();
    if (this.isDateDisabled(today)) {
      return;
    }

    this.viewDate = today;
    this.activePreset = null; // Clear any active preset (like Previous Month)

    if (this.selectionMode === 'multiple') {
      this.selectedDates = [today];
      this.updateInputDisplay();
      this.emitChange(this.selectedDates);
      // Keep strictly open or close? Usually "Today" might implied "Done".
      // But for multiple, user said "shows current date only".
      // Let's close it to be consistent with single mode restart.
      // OR keep open? "Today" usually acts as a selection action.
      // Given "shows current date", implies a reset. I'll close it to match single mode behavior for now.
      // Actually, ref requests didn't specify closing. I'll keep default single mode behavior (close).
    } else if (this.selectionMode === 'range' || this.selectionMode === 'extended') {
      this.rangeStart = today;
      this.rangeEnd = today;

      // Sync atoms
      this.dayValue = today.format('DD');
      this.monthValue = today.format('MM');
      this.yearValue = today.format('YYYY');
      this.endDayValue = today.format('DD');
      this.endMonthValue = today.format('MM');
      this.endYearValue = today.format('YYYY');

      this.updateInputDisplay();
      this.emitChange({ start: today, end: today });
    } else {
      // SINGLE
      this.selectedDate = today;
      // Sync internal field values
      this.dayValue = today.format('DD');
      this.monthValue = today.format('MM');
      this.yearValue = today.format('YYYY');
      this.updateInputDisplay();
      this.emitChange(this.selectedDate);
    }

    this.generateCalendar();

    // Close and destroy dropdown (consistent behavior)
    this.isOpen = false;
    this.viewMode = this.minMode;
    this.destroyDropdown();
    this.onTouched();
    if (this.advanceFocusOnSelect && this.selectionMode === 'single') {
      this.focusNextFocusable();
    }
  }

  /**
   * Moves focus to the next visible focusable element after this picker's input
   * (mirrors app-lookup's commit behaviour). Skips tabindex="-1" fields and this
   * component's own descendants (e.g. the toggle button / any still-mounted
   * calendar). Deferred a tick so the closed calendar is out of the DOM first.
   */
  private focusNextFocusable(): void {
    setTimeout(() => {
      const host = this.elementRef.nativeElement as HTMLElement;
      const input = host.querySelector('.datepicker-input') as HTMLElement | null;
      if (!input || typeof document === 'undefined') {
        return;
      }
      const focusable = Array.from(
        document.querySelectorAll<HTMLElement>('input, select, textarea, button, a[href], [tabindex]'))
        .filter(el => el.tabIndex !== -1
          && !(el as HTMLInputElement).disabled
          && el.getClientRects().length > 0
          && (el === input || !host.contains(el)));
      const here = focusable.indexOf(input);
      if (here !== -1 && focusable[here + 1]) {
        focusable[here + 1].focus();
      }
    }, 0);
  }

  clearDate(emit: boolean = true): void {
    this.selectedDate = null;
    this.selectedDates = [];
    this.rangeStart = null;
    this.rangeEnd = null;
    this.activePreset = null;

    this.inputValue = '';
    // Clear internal field values
    this.dayValue = '';
    this.monthValue = '';
    this.yearValue = '';
    this.endDayValue = '';
    this.endMonthValue = '';
    this.endYearValue = '';

    // Update display to check mode
    this.updateInputDisplay();

    this.viewDate = dayjs();
    this.generateCalendar();

    // Close and destroy dropdown
    this.isOpen = false;
    this.viewMode = 'day';
    this.destroyDropdown();

    if (emit) {
      if (this.selectionMode === 'multiple') {
        this.emitChange([]);
      } else {
        this.emitChange(null);
      }
      this.onTouched();
    }
  }

  closeCalendar(): void {
    this.isOpen = false;
    if (AppDatePickerComponent.openInstance === this) {
      AppDatePickerComponent.openInstance = null;
    }

    // If Period mode and selection is incomplete (Start set but End missing), clear relevant state
    if (this.selectionMode === 'period' && this.rangeStart && !this.rangeEnd) {
      this.rangeStart = null;
      this.dayValue = '';
      this.monthValue = '';
      this.yearValue = '';
      this.updateInputDisplay();
      this.emitChange(null);
    }

    this.destroyDropdown();
    this.onTouched();
  }

  // ============== CONTROL VALUE ACCESSOR ==============

  // ============== CONTROL VALUE ACCESSOR ==============

  // (removed old CVA methods)

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ============== HELPERS ==============

  trackByDay(index: number, day: CalendarDay): string {
    return day.date.format('YYYY-MM-DD');
  }

  writeValue(obj: any): void {
    if (!obj) {
      this.clearDate(false);
      return;
    }

    if (this.selectionMode === 'single') {
      if (dayjs.isDayjs(obj)) {
        this.selectedDate = obj;
      } else if (typeof obj === 'string') {
        this.selectedDate = dayjs(obj);
      } else if (typeof obj === 'number' && this.numberValue) {
        // Treat number as year
        this.selectedDate = dayjs().year(obj).startOf('year');
      }
      if (this.selectedDate) {
        // Sync viewDate to selected date for popup
        this.viewDate = this.selectedDate;
      }
    } else if (this.selectionMode === 'range' || this.selectionMode === 'period') {
      // Handle {start, end} object
      if (obj.start) {
        this.rangeStart = dayjs.isDayjs(obj.start) ? obj.start : dayjs(obj.start);
      }
      if (obj.end) {
        this.rangeEnd = dayjs.isDayjs(obj.end) ? obj.end : dayjs(obj.end);
      }

      // Sync viewDate to start date if available
      if (this.rangeStart) {
        this.viewDate = this.rangeStart;
      }
    } else if (this.selectionMode === 'multiple') {
      if (Array.isArray(obj)) {
        this.selectedDates = obj.map(d => (dayjs.isDayjs(d) ? d : dayjs(d)));
        // Sort
        this.selectedDates.sort((a, b) => a.valueOf() - b.valueOf());
        if (this.selectedDates.length > 0) {
          this.viewDate = this.selectedDates[0];
        }
      }
    }

    this.syncStateToAtoms();
    this.generateCalendar();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private createDropdown(): void {
    if (this.static) {
      return;
    }
    if (!this.dropdownElement) {
      this.embeddedViewRef = this.viewContainerRef.createEmbeddedView(this.dropdownTemplate);
      this.embeddedViewRef.detectChanges();
      const nodes = this.embeddedViewRef.rootNodes;
      const elementNode = nodes.find((n: Node) => n.nodeType === Node.ELEMENT_NODE) as HTMLElement | undefined;
      if (elementNode) {
        this.dropdownElement = elementNode;
        // Hide until positioned — prevents the bottom-left flash of an unpositioned
        // body-appended node (the reported Marriage-modal bug).
        this.dropdownElement.style.setProperty('position', 'fixed', 'important');
        this.dropdownElement.style.setProperty('top', '0', 'important');
        this.dropdownElement.style.setProperty('left', '0', 'important');
        this.dropdownElement.style.setProperty('opacity', '0', 'important');
        this.dropdownElement.style.setProperty('pointer-events', 'none', 'important');
        this.dropdownElement.style.setProperty('z-index', '2147483647', 'important');
        this.dropdownElement.style.setProperty('display', 'block', 'important');
        this.dropdownElement.style.setProperty('visibility', 'hidden', 'important');
        // Body-append so CDK Dialog's transform does not trap position:fixed.
        this.renderer.appendChild(this.document.body, this.dropdownElement);
      } else {
        console.error('[DatePicker] No element node found in rootNodes!');
      }
    }
  }

  /** Show the (already positioned) body-appended calendar. */
  private revealDropdown(): void {
    if (!this.dropdownElement) {
      return;
    }
    this.dropdownElement.style.setProperty('opacity', '1', 'important');
    this.dropdownElement.style.setProperty('visibility', 'visible', 'important');
    this.dropdownElement.style.setProperty('pointer-events', 'auto', 'important');
  }

  private destroyDropdown(): void {
    // Outside-click is bound in both modes — always unbind it, even static
    // (which has no body-appended element to tear down).
    this.unbindOutsideClick();
    if (this.static) {
      return;
    }
    if (this.dropdownElement?.parentNode) {
      this.renderer.removeChild(this.dropdownElement.parentNode, this.dropdownElement);
    }
    this.dropdownElement = null;

    if (this.embeddedViewRef) {
      this.embeddedViewRef.destroy();
      this.embeddedViewRef = null;
    }

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  private handleDigitInput(digit: string): void {
    const isEnd = this.currentRangeSide === 'end';
    const dVal = isEnd ? this.endDayValue : this.dayValue;
    const mVal = isEnd ? this.endMonthValue : this.monthValue;
    const yVal = isEnd ? this.endYearValue : this.yearValue;

    const setDVal = (v: string): string => (isEnd ? (this.endDayValue = v) : (this.dayValue = v));
    const setMVal = (v: string): string => (isEnd ? (this.endMonthValue = v) : (this.monthValue = v));
    const setYVal = (v: string): string => (isEnd ? (this.endYearValue = v) : (this.yearValue = v));

    switch (this.currentField) {
      case 'day':
        // If day is already complete (2 digits), clear and start fresh
        if (dVal.length >= 2) {
          setDVal('');
        }
        if (isEnd ? this.endDayValue.length === 0 : this.dayValue.length === 0) {
          // If first digit > 3, auto-complete with leading 0
          if (parseInt(digit, 10) > 3) {
            setDVal('0' + digit);
            this.moveToNextField();
          } else if (digit === '0') {
            // Don't allow 0 as first digit to be final (need second digit)
            setDVal(digit);
          } else {
            setDVal(digit);
          }
        } else {
          // Validate that resulting day <= 31
          const potentialDay = parseInt((isEnd ? this.endDayValue : this.dayValue) + digit, 10);
          if (potentialDay > 31) {
            // Invalid day - reset and start fresh with new digit
            if (parseInt(digit, 10) > 3) {
              setDVal('0' + digit);
            } else {
              setDVal(digit);
            }
          } else if (potentialDay === 0) {
            // 00 is invalid - keep just the first digit
            return;
          } else {
            setDVal((isEnd ? this.endDayValue : this.dayValue) + digit);
            this.moveToNextField();
          }
        }
        break;
      case 'month':
        // If month is already complete (2 digits), clear and start fresh
        if (mVal.length >= 2) {
          setMVal('');
        }
        if (isEnd ? this.endMonthValue.length === 0 : this.monthValue.length === 0) {
          // If first digit > 1, auto-complete with leading 0
          if (parseInt(digit, 10) > 1) {
            setMVal('0' + digit);
            this.moveToNextField();
          } else if (digit === '0') {
            // Don't allow 0 as first digit to be final (need second digit)
            setMVal(digit);
          } else {
            setMVal(digit);
          }
        } else {
          // Validate that resulting month <= 12
          const potentialMonth = parseInt((isEnd ? this.endMonthValue : this.monthValue) + digit, 10);
          if (potentialMonth > 12) {
            // Invalid month - reset and start fresh with new digit
            if (parseInt(digit, 10) > 1) {
              setMVal('0' + digit);
            } else {
              setMVal(digit);
            }
          } else if (potentialMonth === 0) {
            // 00 is invalid - keep just the first digit
            return;
          } else {
            setMVal((isEnd ? this.endMonthValue : this.monthValue) + digit);
            this.moveToNextField();
          }
        }
        break;
      case 'year': {
        // If year is already complete (4 digits), clear and start fresh
        if (yVal.length >= 4) {
          setYVal('');
        }
        setYVal((isEnd ? this.endYearValue : this.yearValue) + digit);

        // If year is complete (4 digits), validate against minDate/maxDate
        const safeYVal = isEnd ? this.endYearValue : this.yearValue;
        if (safeYVal.length === 4) {
          let year = parseInt(safeYVal, 10);

          // Clamp year to maxDate year if it exceeds
          if (this.maxDate && year > this.maxDate.year()) {
            year = this.maxDate.year();
            setYVal(year.toString());
          }

          // Clamp year to minDate year if below
          if (this.minDate && year < this.minDate.year()) {
            year = this.minDate.year();
            setYVal(year.toString());
          }

          // If this is the start year and we are in range mode, auto-advance to end day
          if (!isEnd && (this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period')) {
            this.moveToNextField();
          }
        }
        break;
      }
    }
    this.updateInputDisplay();
    setTimeout(() => this.selectCurrentField());

    // Validate the complete date against minDate/maxDate
    const currentD = isEnd ? this.endDayValue : this.dayValue;
    const currentM = isEnd ? this.endMonthValue : this.monthValue;
    const currentY = isEnd ? this.endYearValue : this.yearValue;

    if (currentD.length === 2 && currentM.length === 2 && currentY.length === 4) {
      const enteredDate = dayjs(`${currentY}-${currentM}-${currentD}`, 'YYYY-MM-DD');

      if (enteredDate.isValid()) {
        let targetDate: dayjs.Dayjs | null = null;
        // If date exceeds maxDate
        if (this.maxDate && enteredDate.isAfter(this.maxDate, 'day')) {
          targetDate = this.maxDate;
        }

        // If date is before minDate
        if (this.minDate && enteredDate.isBefore(this.minDate, 'day')) {
          targetDate = this.minDate;
        }

        if (targetDate) {
          const d = targetDate.format('DD');
          const m = targetDate.format('MM');
          const y = targetDate.format('YYYY');

          if (isEnd) {
            this.endDayValue = d;
            this.endMonthValue = m;
            this.endYearValue = y;
          } else {
            this.dayValue = d;
            this.monthValue = m;
            this.yearValue = y;
          }
          this.updateInputDisplay();
        }
      }
    }

    // Only trigger valueChanges when all fields are complete
    if (this.selectionMode === 'range' || this.selectionMode === 'extended') {
      // Range mode handled separately in parseAndApplyInput logic above
      this.parseAndApplyInput();
      return;
    }

    if (this.dayValue.length === 2 && this.monthValue.length === 2 && this.yearValue.length === 4) {
      this.parseAndApplyInput();
    }
  }

  private clearCurrentField(): void {
    const isEnd = this.currentRangeSide === 'end';

    switch (this.currentField) {
      case 'day':
        if (isEnd) {
          this.endDayValue = '';
        } else {
          this.dayValue = '';
        }
        break;
      case 'month':
        if (isEnd) {
          this.endMonthValue = '';
        } else {
          this.monthValue = '';
        }
        break;
      case 'year':
        if (isEnd) {
          this.endYearValue = '';
        } else {
          this.yearValue = '';
        }
        break;
    }
    this.updateInputDisplay();

    // If all fields are empty, set value to null
    if (!this.dayValue && !this.monthValue && !this.yearValue) {
      if (this.selectionMode === 'single') {
        this.selectedDate = null;
        this.emitChange(null);
      } else if (this.selectionMode === 'range' || this.selectionMode === 'extended') {
        this.rangeStart = null;
        this.rangeEnd = null;
        this.activePreset = null;
        this.emitChange(null);

        // If end fields are also empty, reset viewDate to today
        if (!this.endDayValue && !this.endMonthValue && !this.endYearValue) {
          this.viewDate = dayjs();
        }

        this.generateCalendar();
      } else {
        // multiple
        this.selectedDates = [];
        this.emitChange([]);
        this.generateCalendar();
      }
    }

    setTimeout(() => this.selectCurrentField());
  }

  private moveToNextField(): void {
    const isRange = this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period';
    const isEnd = this.currentRangeSide === 'end';

    if (this.currentField === 'day') {
      this.currentField = 'month';
    } else if (this.currentField === 'month') {
      this.currentField = 'year';
    } else {
      if (isRange && !isEnd) {
        this.currentRangeSide = 'end';
        this.currentField = 'day';
      }
    }
    setTimeout(() => this.selectCurrentField());
  }

  private moveToPreviousField(): void {
    const isRange = this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period';
    const isEnd = this.currentRangeSide === 'end';

    if (this.currentField === 'year') {
      this.currentField = 'month';
    } else if (this.currentField === 'month') {
      this.currentField = 'day';
    } else {
      if (isRange && isEnd) {
        this.currentRangeSide = 'start';
        this.currentField = 'year';
      }
    }
    setTimeout(() => this.selectCurrentField());
  }

  private selectCurrentField(): void {
    if (!this.inputElement) {
      return;
    }

    let start = 0,
      end = 0;
    const offset = this.currentRangeSide === 'end' ? 14 : 0; // "dd-mm-yyyy to " is 14 chars

    if (this.minMode === 'year') {
      this.currentField = 'year';
      start = 0;
      end = 4;
      // Adjust for range? Year mode might not support range as fully requested but respecting offset
      if (this.currentRangeSide === 'end') {
        // Year range format? year to year (4 + 4 + 4 = 12 chars offset)
        // But current impl assumes full date fields.
        // Assuming standard full date for now based on request "dd or mm or yyyy"
      }
    } else if (this.minMode === 'month') {
      // mm-yyyy
      if (this.currentField === 'day') {
        this.currentField = 'month';
      }
      // offset for month mode range: "mm-yyyy to " is 11 chars?
      // Let's stick to standard full date range for simplicity unless minMode explicitly requested with range

      if (this.currentField === 'month') {
        start = 0;
        end = 2;
      } else {
        start = 3;
        end = 7;
      }
    } else {
      // Default day mode
      switch (this.currentField) {
        case 'day':
          start = 0;
          end = 2;
          break;
        case 'month':
          start = 3;
          end = 5;
          break;
        case 'year':
          start = 6;
          end = 10;
          break;
      }
    }

    // Apply offset
    start += offset;
    end += offset;

    this.inputElement.setSelectionRange(start, end);
  }

  // ... (skipping unchanged code) ...

  private updateInputDisplay(): void {
    const dayDisplay = this.dayValue.length > 0 ? this.dayValue.padStart(2, '0').substring(0, 2) : 'dd';
    const monthDisplay = this.monthValue.length > 0 ? this.monthValue.padStart(2, '0').substring(0, 2) : 'mm';
    const yearDisplay = this.yearValue.length > 0 ? this.yearValue.padEnd(4, 'y').substring(0, 4) : 'yyyy';

    if (this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period') {
      if (this.isMonthPeriod) {
        // month-granularity period reads "Jul 2026 to Jun 2027"
        const sTxt = this.rangeStart ? this.rangeStart.format('MMM YYYY') : 'mmm yyyy';
        const eTxt = this.rangeEnd ? this.rangeEnd.format('MMM YYYY') : 'mmm yyyy';
        this.inputValue = `${sTxt} to ${eTxt}`;
        this.cdr.detectChanges();
        return;
      }
      const endDayDisplay = this.endDayValue.length > 0 ? this.endDayValue.padStart(2, '0').substring(0, 2) : 'dd';
      const endMonthDisplay = this.endMonthValue.length > 0 ? this.endMonthValue.padStart(2, '0').substring(0, 2) : 'mm';
      const endYearDisplay = this.endYearValue.length > 0 ? this.endYearValue.padEnd(4, 'y').substring(0, 4) : 'yyyy';

      this.inputValue = `${dayDisplay}-${monthDisplay}-${yearDisplay} to ${endDayDisplay}-${endMonthDisplay}-${endYearDisplay}`;
    } else if (this.selectionMode === 'multiple') {
      if (this.selectedDates.length > 0) {
        this.inputValue = this.selectedDates.map(d => d.format('DD-MM-YYYY')).join(', ');
      } else {
        this.inputValue = '';
      }
    } else if (this.minMode === 'year') {
      this.inputValue = `${yearDisplay}`;
    } else if (this.minMode === 'month') {
      if (this.monthValue && this.yearValue && !this.monthValue.includes('m') && !this.yearValue.includes('y')) {
        const date = dayjs(`${this.yearValue}-${this.monthValue}-01`);
        this.inputValue = date.isValid() ? date.format('MMMM YYYY') : `${monthDisplay}-${yearDisplay}`;
      } else {
        this.inputValue = `${monthDisplay}-${yearDisplay}`;
      }
    } else {
      this.inputValue = `${dayDisplay}-${monthDisplay}-${yearDisplay}`;
    }
    this.cdr.detectChanges();
  }

  private getDaysForMonth(viewDate: dayjs.Dayjs, side?: 'start' | 'end'): CalendarDay[] {
    const startOfMonth = viewDate.startOf('month');

    // Get the Monday before or on the first day of month
    let startDate: dayjs.Dayjs;
    if (startOfMonth.day() === 0) {
      startDate = startOfMonth.subtract(6, 'day');
    } else {
      startDate = startOfMonth.subtract(startOfMonth.day() - 1, 'day');
    }

    const days: CalendarDay[] = [];
    let currentDate = startDate;

    const isRangeMode = this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period';

    // Helper to check selection
    const isSelected = (date: dayjs.Dayjs): boolean => {
      if (this.selectionMode === 'single') {
        return this.selectedDate ? date.isSame(this.selectedDate, 'day') : false;
      }
      if (this.selectionMode === 'multiple') {
        return this.selectedDates.some(d => d.isSame(date, 'day'));
      }
      if (isRangeMode) {
        return (!!this.rangeStart && date.isSame(this.rangeStart, 'day')) || (!!this.rangeEnd && date.isSame(this.rangeEnd, 'day'));
      }
      return false;
    };

    const isInRange = (date: dayjs.Dayjs): boolean => {
      if (isRangeMode && this.rangeStart) {
        const end = this.rangeEnd ?? this.hoverDate;
        if (end) {
          return (
            (date.isAfter(this.rangeStart, 'day') && date.isBefore(end, 'day')) ||
            (date.isAfter(end, 'day') && date.isBefore(this.rangeStart, 'day'))
          );
        }
      }
      return false;
    };

    // Generate minimum 35 days (5 weeks)
    let totalDays = 35;

    // Check if 5 weeks is enough to show the full month
    // The last day of the 5th week would be startDate + 34 days
    const lastDayOf5thWeek = startDate.add(34, 'day');
    const endOfMonth = viewDate.endOf('month');

    // If the 5th week ends before the end of the month, we need 6 weeks
    if (lastDayOf5thWeek.isBefore(endOfMonth, 'day')) {
      totalDays = 42;
    }

    for (let i = 0; i < totalDays; i++) {
      const isRangeStart = isRangeMode && !!this.rangeStart && currentDate.isSame(this.rangeStart, 'day');
      const isRangeEnd = isRangeMode && !!this.rangeEnd && currentDate.isSame(this.rangeEnd, 'day');

      // Determine disabled state
      let isDisabled = this.isDateDisabled(currentDate);
      if (!isDisabled && side && this.selectionMode === 'period') {
        isDisabled = this.isPeriodDateDisabled(currentDate, side);
      }

      days.push({
        date: currentDate,
        day: currentDate.date(),
        isCurrentMonth: currentDate.month() === viewDate.month(),
        isToday: currentDate.isSame(dayjs(), 'day'),
        isSelected: isSelected(currentDate),
        isDisabled,
        isInRange: isInRange(currentDate),
        isRangeStart,
        isRangeEnd,
      });
      currentDate = currentDate.add(1, 'day');
    }

    return days;
  }

  private generatePeriodCalendar(): void {
    if (this.selectionMode !== 'period') {
      return;
    }

    // Generate Left Calendar (Start)
    if (this.periodStartViewMode === 'day') {
      this.periodStartCalendarDays = this.getDaysForMonth(this.periodStartViewDate, 'start');
    } else if (this.periodStartViewMode === 'year') {
      this.updatePeriodYearRange('start');
    }

    // Generate Right Calendar (End)
    if (this.periodEndViewMode === 'day') {
      this.periodEndCalendarDays = this.getDaysForMonth(this.periodEndViewDate, 'end');
      // Mark range selection across both
    } else if (this.periodEndViewMode === 'year') {
      this.updatePeriodYearRange('end');
    }
  }

  private updatePeriodYearRange(side: 'start' | 'end'): void {
    const viewDate = side === 'start' ? this.periodStartViewDate : this.periodEndViewDate;
    const currentYear = viewDate.year();
    const start = Math.floor(currentYear / 12) * 12;
    const end = start + 11;
    const range = [];
    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    if (side === 'start') {
      this.periodStartYearRangeStart = start;
      this.periodStartYearRange = range;
    } else {
      this.periodEndYearRangeStart = start;
      this.periodEndYearRange = range;
    }
  }

  private updateDropdownPosition(): void {
    // Inline/static mode: the calendar renders as an *ngTemplateOutlet child of
    // `.datepicker-container` (position:relative), so it is positioned purely by
    // CSS (absolute, top:100%) and is ALWAYS visible the frame it renders — the
    // same reliable approach the shared app-lookup uses inside AppModal, where a
    // body-appended `position:fixed` panel gets trapped/hidden by the CDK dialog.
    // This MUST run before the `dropdownElement` guard below: in static mode no
    // element is body-appended (`dropdownElement` stays null), so the guard would
    // otherwise return early and leave `dropdownStyle` at its initial
    // `visibility:hidden` — the calendar would never show.
    if (this.static) {
      this.dropdownStyle = {
        position: 'absolute',
        top: '100%',
        left: '0',
        zIndex: '1050',
        marginTop: '4px',
        visibility: 'visible',
      };
      return;
    }

    // Guard: If dropdown element not created yet, skip positioning
    if (!this.dropdownElement) {
      return;
    }

    // Calculate fixed position relative to viewport
    let element = this.elementRef.nativeElement.querySelector('.datepicker-input-wrapper');
    if (!element) {
      element = this.elementRef.nativeElement;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = this.selectionMode === 'period' ? 450 : 350; // Approximate dropdown height

    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    let topPosition: number;
    let isFlipped = false;

    // STRICTLY FAVOR BELOW
    if (spaceBelow < 50 && spaceAbove > dropdownHeight) {
      topPosition = rect.top - dropdownHeight - 4;
      isFlipped = true;
    } else {
      // Default Below
      topPosition = rect.bottom + 4;
    }

    // DEBUG: Log coordinates to verify it's trying to render
    // console.log(`Datepicker Pos: Top=${topPosition}, Left=${rect.left}, ZIndex=2147483647, Flipped=${isFlipped}`);

    // Clamp inside the viewport: the panel is wider than the input (the dual
    // period panes especially), so left-aligning to an input near the right
    // edge pushed it off-screen / outside the hosting modal.
    const leftPosition = this.clampDropdownLeft(rect.left);

    // Direct DOM manipulation with PRIORITY to ensure visibility
    const zIndex = '2147483647';

    this.dropdownElement.style.setProperty('position', 'fixed', 'important');
    this.dropdownElement.style.setProperty('top', `${topPosition}px`, 'important');
    this.dropdownElement.style.setProperty('left', `${leftPosition}px`, 'important');
    this.dropdownElement.style.setProperty('z-index', zIndex, 'important');
    this.dropdownElement.style.setProperty('visibility', 'visible', 'important');
    this.dropdownElement.style.setProperty('display', 'block', 'important'); // Safety

    // Still update style object for framework consistency
    this.dropdownStyle = {
      position: 'fixed',
      top: `${topPosition}px`,
      left: `${leftPosition}px`,
      visibility: 'visible',
      zIndex,
    };

    // Reposition once more next frame (layout settles after body-append);
    // do NOT loop forever — that burned a rAF every frame while open.
    if (this.isOpen && this.animationFrame == null) {
      this.ngZone.runOutsideAngular(() => {
        this.animationFrame = requestAnimationFrame(() => {
          this.animationFrame = null;
          if (this.isOpen) {
            // one-shot remeasure only
            const el = this.dropdownElement;
            if (!el) { return; }
            const host = this.elementRef.nativeElement.querySelector('.datepicker-input-wrapper')
              || this.elementRef.nativeElement;
            const rect = (host as HTMLElement).getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = this.selectionMode === 'period' ? 450 : 350;
            const top = (spaceBelow < 50 && rect.top > dropdownHeight)
              ? rect.top - dropdownHeight - 4
              : rect.bottom + 4;
            el.style.setProperty('top', `${top}px`, 'important');
            el.style.setProperty('left', `${this.clampDropdownLeft(rect.left)}px`, 'important');
          }
        });
      });
    }
  }

  /** Keeps the body-appended panel fully on-screen horizontally. Measures the
   *  real panel when it is already laid out; falls back to a mode estimate. */
  private clampDropdownLeft(preferredLeft: number): number {
    const width = (this.dropdownElement && this.dropdownElement.offsetWidth)
      || (this.selectionMode === 'period' ? 560 : 320);
    return Math.max(8, Math.min(preferredLeft, window.innerWidth - width - 8));
  }

  // CVA callbacks
  // CVA callbacks
  private propagateChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  private emitChange(value: any): void {
    let outputValue = value;
    if (this.valueFormat && value) {
      if (dayjs.isDayjs(value)) {
        outputValue = value.format(this.valueFormat);
      } else if (Array.isArray(value)) {
        outputValue = (value as unknown[]).map(v => (dayjs.isDayjs(v) ? v.format(this.valueFormat!) : v));
      }
    } else if (this.numberValue && value && dayjs.isDayjs(value)) {
      outputValue = value.year();
    }
    this.propagateChange(outputValue);
    this.dateSelect.emit(outputValue);
  }

  private formatInputValue(digits: string): string {
    if (!digits) {
      return '';
    }

    let formatted = '';
    if (digits.length >= 2) {
      formatted = digits.substring(0, 2) + '-';
      if (digits.length >= 4) {
        formatted += digits.substring(2, 4) + '-';
        formatted += digits.substring(4, 8);
      } else {
        formatted += digits.substring(2, 4);
      }
    } else {
      formatted = digits;
    }
    return formatted;
  }

  private getNextCursorPosition(digitCount: number): number {
    if (digitCount <= 2) {
      return digitCount;
    }
    if (digitCount <= 4) {
      return digitCount + 1; // After first hyphen
    }
    return digitCount + 2; // After both hyphens
  }

  private parseAndApplyInput(): void {
    // Handle Multiple Mode Parsing from raw input
    if (this.selectionMode === 'multiple') {
      const rawValue = this.inputElement ? this.inputElement.value : this.inputValue;
      if (!rawValue) {
        this.selectedDates = [];
        this.emitChange(this.selectedDates);
        return;
      }

      const parts = rawValue.split(',');
      const newDates: dayjs.Dayjs[] = [];

      parts.forEach(part => {
        const trimmed = part.trim();
        // Simple strict parsing for DD-MM-YYYY or flexible
        const date = dayjs(trimmed, 'DD-MM-YYYY');
        // Note: strict mode in dayjs might be needed if format is variable
        if (date.isValid() && !this.isDateDisabled(date)) {
          // Check duplicates? Array lookup
          if (!newDates.some(d => d.isSame(date, 'day'))) {
            newDates.push(date);
          }
        }
      });

      // Sort dates chronologically
      newDates.sort((a, b) => a.valueOf() - b.valueOf());

      this.selectedDates = newDates;
      this.emitChange(this.selectedDates);
      this.generateCalendar();
      return;
    }

    // Handle Range/Period Mode Parsing
    if (this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period') {
      // Construct dates from atomic fields

      // Check if start is fully entered
      const sDay = this.dayValue.padStart(2, '0');
      const sMonth = this.monthValue.padStart(2, '0');
      const sYear = this.yearValue;

      const eDay = this.endDayValue.padStart(2, '0');
      const eMonth = this.endMonthValue.padStart(2, '0');
      const eYear = this.endYearValue;

      const startComplete = this.dayValue.length === 2 && this.monthValue.length === 2 && this.yearValue.length === 4;
      const endComplete = this.endDayValue.length === 2 && this.endMonthValue.length === 2 && this.endYearValue.length === 4;

      let start = startComplete ? dayjs(`${sYear}-${sMonth}-${sDay}`, 'YYYY-MM-DD') : null;
      if (start && !start.isValid()) {
        start = null;
      }

      let end = endComplete ? dayjs(`${eYear}-${eMonth}-${eDay}`, 'YYYY-MM-DD') : null;
      if (end && !end.isValid()) {
        end = null;
      }

      // Validate against maxDate (if provided)
      if (this.maxDate) {
        if (start?.isAfter(this.maxDate, 'day')) {
          start = this.maxDate;
          // Update atoms
          this.dayValue = start.format('DD');
          this.monthValue = start.format('MM');
          this.yearValue = start.format('YYYY');
        }
        if (end?.isAfter(this.maxDate, 'day')) {
          end = this.maxDate;
          // Update atoms
        }
      }

      if (start) {
        // Auto-correction logic for end date if end is fully entered
        if (end) {
          // Start date must be before or same as End Date
          if (end.isBefore(start, 'day')) {
            // Clamp End Date to Start Date (or should we reset?)
            // User request: "ensure end date part is greater than start date part."
            // We set end = start to satisfy >= constraint.
            end = start.clone();
          }

          // Update atoms for end (in case clamped by maxDate or start date)
          this.endDayValue = end.format('DD');
          this.endMonthValue = end.format('MM');
          this.endYearValue = end.format('YYYY');
        }

        this.rangeStart = start;
        this.rangeEnd = end;
        this.viewDate = start;
        this.emitChange({ start: this.rangeStart, end: this.rangeEnd });
        this.activePreset = null; // Clear preset since we have manual input
        this.generateCalendar();

        // Only re-render input if we are not currently typing?
        // If we change values (auto-correct), we certainly must.
        // But normally updateInputDisplay is called by handleDigitInput anyway.
        // We need to call it here to reflect auto-correction.
        this.updateInputDisplay();
      } else {
        // Start invalid or incomplete
        this.rangeStart = null;
        this.rangeEnd = null;
        this.activePreset = null; // Clear preset since input is broken/manual
        this.emitChange(null);
        this.generateCalendar();

        // If all fields are empty, reset viewDate to today to restore context
        if (!this.dayValue && !this.monthValue && !this.yearValue && !this.endDayValue && !this.endMonthValue && !this.endYearValue) {
          this.viewDate = dayjs();
          this.generateCalendar(); // Re-generate with new viewDate
        }
      }
      return;
    }

    // Single Mode Logic (Existing)
    // If all fields are empty, clear the value
    if (!this.dayValue && !this.monthValue && !this.yearValue) {
      this.selectedDate = null;
      this.viewDate = dayjs();
      this.generateCalendar();
      this.emitChange(null);
      this.onTouched();
      return;
    }

    // Pad values to ensure valid ISO format YYYY-MM-DD
    let dayStr = this.dayValue.padStart(2, '0');
    const monthStr = this.monthValue.padStart(2, '0');
    const yearStr = this.yearValue;

    // Handle defaults for minMode
    if (this.minMode === 'month' || this.minMode === 'year') {
      if (!this.dayValue) {
        dayStr = '01'; // Default to first of month
      }
    }

    let finalMonthStr = monthStr;
    if (this.minMode === 'year' && (!this.monthValue || this.monthValue === '00')) {
      finalMonthStr = '01';
    }
    if ((this.minMode === 'month' || this.minMode === 'year') && (!this.dayValue || this.dayValue === '00')) {
      dayStr = '01';
    }

    // Ensure we have enough data based on mode
    let isValidLength = false;
    if (this.minMode === 'year') {
      isValidLength = yearStr.length === 4;
    } else if (this.minMode === 'month') {
      isValidLength = yearStr.length === 4 && parseInt(finalMonthStr, 10) > 0;
    } else {
      isValidLength = yearStr.length === 4 && parseInt(finalMonthStr, 10) > 0 && parseInt(dayStr, 10) > 0;
    }

    if (isValidLength) {
      const date = dayjs(`${yearStr}-${finalMonthStr}-${dayStr}`, 'YYYY-MM-DD');

      if (date.isValid()) {
        // Check if date is disabled
        if (this.isDateDisabled(date)) {
          // Proceed but possibly mark invalid?
        }

        this.selectedDate = date;
        this.viewDate = date;
        this.generateCalendar();
        this.emitChange(date);
        this.onTouched();
      } else {
        // Invalid date entered fully
        this.selectedDate = null;
        this.emitChange(null);
        this.onTouched();
      }
    }
  }

  private isValidDate(day: number, month: number, year: number): boolean {
    if (month < 1 || month > 12) {
      return false;
    }
    if (day < 1 || day > 31) {
      return false;
    }
    if (year < 1900 || year > 2100) {
      return false;
    }
    // Check days in month
    const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth();
    if (day > daysInMonth) {
      return false;
    }
    // Check min/max
    const date = dayjs(`${year}-${month}-${day}`);
    if (this.minDate && date.isBefore(this.minDate, 'day')) {
      return false;
    }
    if (this.maxDate && date.isAfter(this.maxDate, 'day')) {
      return false;
    }
    return true;
  }

  private isPeriodDateDisabled(date: dayjs.Dayjs, side: 'start' | 'end'): boolean {
    if (side === 'end') {
      // Disable dates before rangeStart if exists
      if (this.rangeStart && date.isBefore(this.rangeStart, 'day')) {
        return true;
      }
    }
    return false;
  }

  private isDateDisabled(date: dayjs.Dayjs): boolean {
    if (this.minDate && date.isBefore(this.minDate, 'day')) {
      return true;
    }
    if (this.maxDate && date.isAfter(this.maxDate, 'day')) {
      return true;
    }

    // Check disabled dates
    if (this.disabledDates.some(d => d.isSame(date, 'day'))) {
      return true;
    }

    // Check disabled ranges
    if (this.disabledRanges.some(r => !date.isBefore(r.start, 'day') && !date.isAfter(r.end, 'day'))) {
      return true;
    }

    return false;
  }

  private syncStateToAtoms(): void {
    if (this.selectionMode === 'single') {
      if (this.selectedDate) {
        this.dayValue = this.selectedDate.format('DD');
        this.monthValue = this.selectedDate.format('MM');
        this.yearValue = this.selectedDate.format('YYYY');
      } else {
        this.dayValue = '';
        this.monthValue = '';
        this.yearValue = '';
      }
    } else if (this.selectionMode === 'range' || this.selectionMode === 'extended' || this.selectionMode === 'period') {
      if (this.rangeStart) {
        this.dayValue = this.rangeStart.format('DD');
        this.monthValue = this.rangeStart.format('MM');
        this.yearValue = this.rangeStart.format('YYYY');
      } else {
        this.dayValue = '';
        this.monthValue = '';
        this.yearValue = '';
      }

      if (this.rangeEnd) {
        this.endDayValue = this.rangeEnd.format('DD');
        this.endMonthValue = this.rangeEnd.format('MM');
        this.endYearValue = this.rangeEnd.format('YYYY');
      } else {
        this.endDayValue = '';
        this.endMonthValue = '';
        this.endYearValue = '';
      }
    }
    this.updateInputDisplay();
  }
}
